import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../utils/api';

const CatalogosContext = createContext(null);

export const CatalogosProvider = ({ children }) => {
  const [catalogos, setCatalogos] = useState({
    categorias: [],
    lugares: [],
    items: [],
    fallas: []
  });
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  // Cargar catálogos (con cache de 5 minutos)
  const loadCatalogos = useCallback(async (forceRefresh = false) => {
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    const now = Date.now();
    
    // Si ya está cargado y no ha expirado el cache, no recargar
    if (!forceRefresh && loaded && lastFetch && (now - lastFetch < CACHE_DURATION)) {
      return catalogos;
    }
    
    // Si ya está cargando, esperar
    if (loading) {
      return catalogos;
    }
    
    setLoading(true);
    try {
      // Usar el nuevo endpoint unificado
      const response = await api.get('/catalogos/all');
      const data = response.data || {};
      
      const newCatalogos = {
        categorias: data.categorias || [],
        lugares: data.lugares || [],
        items: data.items || [],
        fallas: data.fallas || []
      };
      
      setCatalogos(newCatalogos);
      setLoaded(true);
      setLastFetch(now);
      return newCatalogos;
    } catch (error) {
      console.error('Error loading catalogos:', error);
      return catalogos;
    } finally {
      setLoading(false);
    }
  }, [catalogos, loading, loaded, lastFetch]);

  // Forzar recarga (después de crear/editar/eliminar items del catálogo)
  const refreshCatalogos = useCallback(() => {
    return loadCatalogos(true);
  }, [loadCatalogos]);

  // Invalidar cache
  const invalidateCache = useCallback(() => {
    setLoaded(false);
    setLastFetch(null);
  }, []);

  return (
    <CatalogosContext.Provider value={{
      catalogos,
      loading,
      loaded,
      loadCatalogos,
      refreshCatalogos,
      invalidateCache
    }}>
      {children}
    </CatalogosContext.Provider>
  );
};

export const useCatalogos = () => {
  const context = useContext(CatalogosContext);
  if (!context) {
    throw new Error('useCatalogos must be used within a CatalogosProvider');
  }
  return context;
};

export default CatalogosContext;
